import { useParams } from 'react-router-dom'
import {
  BfnStandortBox,
  Button,
  ErrorAlert,
  GoBackButton,
  Heading,
  LoadingScreen
} from '@repo/ui'
import { DoneIcon32 } from '@repo/ui/icons'
import BfnStatusTag from '@/features/befehlsnachrichten/components/BfnStatusTag'
import { BfnStatus } from '@/models/Befehlsnachricht'
import BfnStandorteingabe from '@/features/befehlsnachrichten/components/BfnStandorteingabe'
import { useTranslation } from 'react-i18next'
import { useBfnStore } from '@/store/useBfnStore'
import BfnKopfdatenBox from '@/features/befehlsnachrichten/components/BfnKopfdatenBox'
import BfnBefehleBox from '@/features/befehlsnachrichten/components/BfnBefehleBox'
import BefehleAlleGelesenProvider from '@/features/befehlsnachrichten/providers/BefehleAlleGelesenProvider'
import { useBefehlsnachrichtHashVerification } from '@/features/befehlsnachrichten/hooks/useBefehlsnachrichtHashVerification'
import BfnDetailsErrorPage from '@/features/befehlsnachrichten/components/details/BfnDetailsErrorPage'
import { checkVisuellerStatusMatchesStatus } from '@/features/befehlsnachrichten/utils/befehlsnachricht-status'
import { useShallow } from 'zustand/react/shallow'
import BfnDetailsSideButtonsMenu from '@/features/befehlsnachrichten/components/BfnDetailsSideButtonsMenu'
import { checkAbruftokenExpired } from '@/features/befehlsnachrichten/utils/abruftoken'
import { useMutationState } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useScrollProgressColor } from '../../../providers/ScrollProgressColorProvider'

export default function BfnDetails() {
  const { t } = useTranslation(['bfnDetails', 'bfn'])
  const { id: paramsBfnId } = useParams()
  const { updateProgressStyle } = useScrollProgressColor()

  const testIdInit = `bfn-details`
  const bfnDataEntry = useBfnStore(
    useShallow((state) => (paramsBfnId ? state.data[paramsBfnId] : null))
  )
  const befehlsnachricht = bfnDataEntry?.befehlsnachricht
  const { isHashValid } = useBefehlsnachrichtHashVerification(befehlsnachricht)

  // Get PDF download mutation state
  const pdfMutations = useMutationState({
    filters: {
      mutationKey: ['befehlsnachricht-pdf-download', befehlsnachricht?.id],
      exact: true
    },
    select: (mutation) => mutation.state
  })
  const latestPdfMutation = pdfMutations.at(-1)
  const pdfDownloadError = latestPdfMutation?.status === 'error'
  const pdfDownloadErrorDetails = latestPdfMutation?.error as Error | undefined

  const [isStandortGelesen, setIsStandortGelesen] = useState(false)
  const onStandortGelesenClick = () => {
    setIsStandortGelesen(true)
  }

  useEffect(() => {
    if (befehlsnachricht) {
      updateProgressStyle(
        befehlsnachricht.status,
        !!bfnDataEntry.manuellWiderrufenAt
      )
    }
  }, [befehlsnachricht, bfnDataEntry?.manuellWiderrufenAt, updateProgressStyle])

  if (!bfnDataEntry) {
    return (
      <BfnDetailsErrorPage testId={testIdInit}>
        {t('error.not_found', { id: paramsBfnId })}
      </BfnDetailsErrorPage>
    )
  }

  const { abruftoken } = bfnDataEntry
  const isTokenExpired = checkAbruftokenExpired(abruftoken)

  if (!befehlsnachricht) {
    // Datensatz mit Abruftoken vorhanden, aber Befehlsnachricht fehlt. Die Befehlsnachricht wird vom `BefehlsnachrichtUpdater` im Hintergrund abgerufen.
    return (
      <LoadingScreen
        text={t('loading.befehlsnachricht')}
        data-testid={`${testIdInit}-loading`}
      />
    )
  }

  if (isHashValid === null) {
    return <LoadingScreen data-testid={`${testIdInit}-loading`} />
  }

  if (!isHashValid) {
    return (
      <BfnDetailsErrorPage testId={testIdInit}>
        {t('error.invalid_hash', { id: befehlsnachricht.id })}
      </BfnDetailsErrorPage>
    )
  }

  if (
    checkVisuellerStatusMatchesStatus(
      befehlsnachricht.visuellerStatus,
      befehlsnachricht.status
    ).error
  ) {
    return (
      <BfnDetailsErrorPage testId={testIdInit}>
        {t('error.visueller_status_mismatch', {
          visuellerStatus: befehlsnachricht.visuellerStatus,
          status: befehlsnachricht.status
        })}
      </BfnDetailsErrorPage>
    )
  }

  if (befehlsnachricht.status === BfnStatus.ERSTABRUF_GESTARTET) {
    return (
      <BfnStandorteingabe
        abruftoken={abruftoken}
        bfn={befehlsnachricht}
        data-testid={`${testIdInit}-standort-eingabe-form`}
      />
    )
  }

  return (
    <>
      <div className="flex flex-col lg:flex-row gap-0.5 justify-between items-baseline">
        <div className="flex items-center gap-0.5">
          <GoBackButton data-testid={`${testIdInit}-go-back-button`} />
          <Heading tag="h1">
            {t('title_with_name', { name: befehlsnachricht.eindeutigeKennung })}
          </Heading>
        </div>
      </div>

      <div className="flex">
        <BfnStatusTag
          status={befehlsnachricht.status}
          visuellerStatus={befehlsnachricht.visuellerStatus}
          manuellWiderrufen={!!bfnDataEntry.manuellWiderrufenAt}
          data-testid={`${testIdInit}-status`}
        />
      </div>

      {pdfDownloadError && (
        <ErrorAlert
          className="mt-1 w-full"
          data-testid={`${testIdInit}-pdf-download-error`}
          error={pdfDownloadErrorDetails}
        >
          {t('bfn:errors.pdf_export_failed')}
        </ErrorAlert>
      )}

      <BefehleAlleGelesenProvider>
        <div className="mt-1 flex flex-col items-stretch">
          <div className="flex flex-col md:flex-row md:gap-2">
            <div className="flex flex-col items-start gap-0.5 md:w-4/6">
              <BfnKopfdatenBox
                bfn={befehlsnachricht}
                data-testid={`${testIdInit}-zugdatenbox`}
              />
              {befehlsnachricht.standort && (
                <BfnStandortBox
                  standort={befehlsnachricht.standort}
                  data-testid={`${testIdInit}-standortbox`}
                >
                  {befehlsnachricht.status ===
                    BfnStatus.STANDORTBESTAETIGUNG_DURCH_TF_ERFORDERLICH && (
                    <Button
                      variant="brand-primary"
                      className="w-full mt-0.75"
                      onClick={onStandortGelesenClick}
                      disabled={isStandortGelesen}
                      data-testid={`${testIdInit}-gelesen-button`}
                    >
                      <DoneIcon32
                        className="shrink-0"
                        data-testid={`${testIdInit}-gelesen-button-icon`}
                      />
                      <span
                        className="pl-0.5"
                        data-testid={`${testIdInit}-gelesen-button-text`}
                      >
                        {t('standort_gelesen_erforderlich.button_text')}
                      </span>
                    </Button>
                  )}
                </BfnStandortBox>
              )}
              <BfnBefehleBox
                bfnStatus={befehlsnachricht.status}
                befehle={befehlsnachricht.befehle}
                isTokenExpired={isTokenExpired}
                data-testid={`${testIdInit}-befehlebox`}
              />
            </div>
            <BfnDetailsSideButtonsMenu
              abruftoken={abruftoken}
              befehlsnachricht={befehlsnachricht}
              bfnDataEntry={bfnDataEntry}
              isStandortGelesen={isStandortGelesen}
              data-testid={testIdInit}
            />
          </div>
        </div>
      </BefehleAlleGelesenProvider>
    </>
  )
}
